import { useState } from 'react';
import type { AccountUserRoleUser } from 'vopay';
import { api } from '@/services/api.config';

/**
 * Direct Pattern hook for fetching, per role, how many users are assigned and
 * who they are. Backend does not offer a bulk endpoint, so this fans out one
 * call per role via Promise.all. Acceptable while role counts per account
 * stay small (~10).
 *
 * Returns:
 *   counts:      Record<RoleID, number>          — for the Roles tile badges
 *   usersByRole: Record<RoleID, AccountUserRoleUser[]> — invert this to
 *     populate the Role(s) column on the Users table.
 */
export default function useFetchRoleUserCounts() {
  const [fetching, setFetching] = useState(false);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [usersByRole, setUsersByRole] = useState<Record<number, AccountUserRoleUser[]>>({});

  async function fetchRoleUserCounts(roleIds: number[], bearer?: string) {
    if (roleIds.length === 0) {
      setCounts({});
      setUsersByRole({});

      return {};
    }

    try {
      setFetching(true);

      // Use `allSettled` so one failed role-fetch doesn't blank every tile.
      // Partial failures default to count=0 / empty users; the per-role fetch
      // on the Role Details drawer's delete confirmation still gives the
      // accurate number when it matters.
      const settled = await Promise.allSettled(
        roleIds.map(async (RoleID) => {
          const res = await api.fetchUsersByRole({ RoleID }, bearer);

          if (!res.Success) {
            throw new Error(res.ErrorMessage);
          }

          return res.Users;
        }),
      );

      const nextUsersByRole: Record<number, AccountUserRoleUser[]> = {};
      const nextCounts: Record<number, number> = {};

      settled.forEach((result, i) => {
        const RoleID = roleIds[i];

        if (result.status === 'fulfilled') {
          const users = result.value;
          nextUsersByRole[RoleID] = users;
          nextCounts[RoleID] = users.length;
        } else {
          // Per-role failure: surface the reason but keep the tile rendering.
          console.error(`Fetch users for role ${RoleID} failed:`, result.reason);
          nextUsersByRole[RoleID] = [];
          nextCounts[RoleID] = 0;
        }
      });

      setUsersByRole(nextUsersByRole);
      setCounts(nextCounts);

      return nextCounts;
    } finally {
      setFetching(false);
    }
  }

  function reset() {
    setCounts({});
    setUsersByRole({});
  }

  return { fetching, counts, usersByRole, fetchRoleUserCounts, reset };
}
